/**
 * The token economics view's model (CostsPage.tsx): the period and the filters in the address, where each ranked thing
 * opens, and the words for amounts. Pure functions, so the page and its charts read one set of rules.
 */

import { costJobKey, type CostBillingFilter, type CostGrouping, type CostRank, type CostTotals } from '@aioe/shared';

import type { CostFilters } from '../../api/client.ts';
import { liveHref } from '../live/liveModel.ts';

export const RANGE_KEYS = ['7d', '30d', '90d', 'month', 'last-month', 'year'] as const;
export type RangeKey = (typeof RANGE_KEYS)[number];
export const RANGE_LABELS: Record<RangeKey, string> = { '7d': '7d', '30d': '30d', '90d': '90d', month: 'This month', 'last-month': 'Last month', year: 'This year' };
export const RANGE_WORDS: Record<RangeKey, string> = { '7d': 'the last 7 days', '30d': 'the last 30 days', '90d': 'the last 90 days', month: 'this month', 'last-month': 'last month', year: 'this year' };
export const isRangeKey = (k: string | null | undefined): k is RangeKey => Boolean(k) && (RANGE_KEYS as readonly string[]).includes(k!);

/** A range's bounds at a moment: rolling days end now; months and the year are whole UTC ones. */
export function rangeBounds(range: RangeKey, now: Date): { since: string; until: string } {
  const day = 86_400_000;
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (range) {
    case '7d':
      return { since: new Date(now.getTime() - 7 * day).toISOString(), until: now.toISOString() };
    case '30d':
      return { since: new Date(now.getTime() - 30 * day).toISOString(), until: now.toISOString() };
    case '90d':
      return { since: new Date(now.getTime() - 90 * day).toISOString(), until: now.toISOString() };
    case 'month':
      return { since: new Date(Date.UTC(y, m, 1)).toISOString(), until: now.toISOString() };
    case 'last-month':
      return { since: new Date(Date.UTC(y, m - 1, 1)).toISOString(), until: new Date(Date.UTC(y, m, 1)).toISOString() };
    case 'year':
      return { since: new Date(Date.UTC(y, 0, 1)).toISOString(), until: now.toISOString() };
  }
}

/** The filters the address carries, as the costs routes take them. `repository` is `<projectId>:<rootId>`. */
export interface CostView {
  range?: RangeKey;
  since?: string;
  until?: string;
  billing: CostBillingFilter;
  areaId?: string;
  projectId?: string;
  teamId?: string;
  repository?: string;
  model?: string;
}

export const FILTER_PARAMS = ['range', 'since', 'until', 'billing', 'area', 'project', 'team', 'repository', 'model'] as const;

export function readView(p: URLSearchParams): CostView {
  const since = p.get('since') ?? undefined;
  const billing = p.get('billing');
  return {
    ...(since ? { since, ...(p.get('until') ? { until: p.get('until')! } : {}) } : { range: isRangeKey(p.get('range')) ? (p.get('range') as RangeKey) : '30d' }),
    billing: billing === 'metered' || billing === 'subscription' ? billing : 'all',
    ...(p.get('area') ? { areaId: p.get('area')! } : {}),
    ...(p.get('project') ? { projectId: p.get('project')! } : {}),
    ...(p.get('team') ? { teamId: p.get('team')! } : {}),
    ...(p.get('repository') ? { repository: p.get('repository')! } : {}),
    ...(p.get('model') ? { model: p.get('model')! } : {}),
  };
}

/** The view written back into the address, defaults left out. */
export function writeView(p: URLSearchParams, v: CostView): URLSearchParams {
  const n = new URLSearchParams(p);
  for (const k of FILTER_PARAMS) n.delete(k);
  if (v.since) {
    n.set('since', v.since);
    if (v.until) n.set('until', v.until);
  } else if (v.range && v.range !== '30d') n.set('range', v.range);
  if (v.billing !== 'all') n.set('billing', v.billing);
  if (v.areaId) n.set('area', v.areaId);
  if (v.projectId) n.set('project', v.projectId);
  if (v.teamId) n.set('team', v.teamId);
  if (v.repository) n.set('repository', v.repository);
  if (v.model) n.set('model', v.model);
  return n;
}

/** The query the routes take for a view at a moment. */
export function filtersOf(v: CostView, now: Date): CostFilters {
  const bounds = v.since ? { since: v.since, until: v.until ?? now.toISOString() } : rangeBounds(v.range ?? '30d', now);
  const at = v.repository?.indexOf(':') ?? -1;
  const repoProject = v.repository && at >= 0 ? v.repository.slice(0, at) : undefined;
  const rootId = v.repository ? (at >= 0 ? v.repository.slice(at + 1) : v.repository) : undefined;
  return {
    ...bounds,
    billing: v.billing,
    ...(v.areaId ? { areaId: v.areaId } : {}),
    ...(v.projectId || repoProject ? { projectId: v.projectId ?? repoProject } : {}),
    ...(v.teamId ? { teamId: v.teamId } : {}),
    ...(rootId ? { rootId } : {}),
    ...(v.model ? { model: v.model } : {}),
  };
}

/** How many filters past the period and the calls are set. */
export const filterCount = (v: CostView) => [v.areaId, v.projectId, v.teamId, v.repository, v.model].filter(Boolean).length + (v.billing !== 'all' ? 1 : 0);

/** Where a ranked thing opens: its team or agent page, its project or repository, its live session. Undefined for nothing to open. */
export function rankHref(by: CostGrouping | 'work', r: CostRank): string | undefined {
  if (r.rest || !r.key) return undefined;
  switch (by) {
    case 'project':
      return `/projects/${encodeURIComponent(r.key)}`;
    case 'team':
      return `/teams/${encodeURIComponent(r.key)}`;
    case 'member':
      if (!r.teamId) return undefined;
      return r.kind === 'specialist' || !r.memberName ? `/teams/${encodeURIComponent(r.teamId)}` : `/teams/${encodeURIComponent(r.teamId)}/members/${encodeURIComponent(r.memberName)}`;
    case 'repository':
      return r.projectId ? `/projects/${encodeURIComponent(r.projectId)}?tab=code&part=repositories` : undefined;
    case 'session': {
      if (r.kind === 'session' && r.deviceId) return liveHref(r.deviceId, { sessionId: r.key.slice('session:'.length) }, r.projectId);
      if (r.kind === 'workflow' && r.projectId) return `/apps-and-routines/workflow-runs/${encodeURIComponent(r.projectId)}/${encodeURIComponent(r.key.slice('workflow:'.length))}`;
      return r.projectId ? `/projects/${encodeURIComponent(r.projectId)}` : undefined;
    }
    case 'device':
      return `/workbenches/${encodeURIComponent(r.key)}`;
    default:
      return undefined;
  }
}

/** The job key a session id makes (costJobKey), for links that come from elsewhere. */
export const sessionKey = (sessionId: string) => costJobKey({ sessionId });

/** Which money a chart weighs by: metered spend while there is any, else tokens (an organisation on paid seats only). */
export type Measure = 'usd' | 'tokens';
export const measureOf = (t: CostTotals | undefined): Measure => (t && t.usd > 0 ? 'usd' : 'tokens');
export const valueOf = (t: CostTotals, m: Measure) => (m === 'usd' ? t.usd : t.tokens);

export const compact = (n: number) => n.toLocaleString('en-AU', { notation: 'compact', maximumFractionDigits: 1 });
export const percentWords = (x: number | null | undefined, digits = 0) => (x === null || x === undefined ? '—' : `${(x * 100).toFixed(digits)}%`);

/** "4h 12m", "35m", "under a minute": how long a job ran from its first call to its last. */
export function spanWords(firstAt?: string, lastAt?: string): string | undefined {
  if (!firstAt || !lastAt) return undefined;
  const ms = Date.parse(lastAt) - Date.parse(firstAt);
  if (!Number.isFinite(ms)) return undefined;
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.round(h / 24)} days`;
}

/** The change from the period before, in words: "+12% on before", "new this period", "". */
export function changeWords(now: number, before: number): string {
  if (before > 0) {
    const pct = Math.round(((now - before) / before) * 100);
    return `${pct >= 0 ? '+' : ''}${pct}% on before`;
  }
  return now > 0 ? 'Nothing the period before' : '';
}
